import {
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HomeOutlined,
  LinkOutlined,
  MoreOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Card, Dropdown, Tag, Typography } from 'antd';
import firebase from 'firebase/app';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { firestore } from '../../firebase/firebase';
import { WantedListing } from '../../models/wantedListings.model';
import { useAuth } from '../Auth/AuthContext';

const { Text, Title } = Typography;

interface WantedListingCardProps {
  listing: WantedListing;
  companyName?: string;
  onOfferClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isOwner?: boolean;
}

export const WantedListingCard: React.FC<WantedListingCardProps> = ({
  listing,
  companyName,
  onOfferClick,
  onEdit,
  onDelete,
  isOwner,
}) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation('wanted-listings');

  // Funkcja pomocnicza do formatowania URL
  const formatUrl = (url: string) => {
    if (!url) return '';
    if (!url.match(/^https?:\/\//i)) {
      return `https://${url}`;
    }
    return url;
  };

  // Funkcja do wyświetlania tylko domeny
  const displayDomain = (url: string) => {
    if (!url) return '';
    try {
      const urlObj = new URL(formatUrl(url));
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Funkcja do formatowania daty na "X dni temu"
  const formatTimeAgo = (timestamp: firebase.firestore.Timestamp | firebase.firestore.FieldValue): string => {
    if (!(timestamp instanceof firebase.firestore.Timestamp)) {
      return t('TIME_AGO.JUST_NOW');
    }

    const now = new Date();
    const date = timestamp.toDate();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return t('TIME_AGO.TODAY');
    } else if (diffInDays === 1) {
      return t('TIME_AGO.YESTERDAY');
    } else if (diffInDays < 7) {
      return t('TIME_AGO.DAYS_AGO', { days: diffInDays });
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return weeks === 1 ? t('TIME_AGO.WEEK_AGO') : t('TIME_AGO.WEEKS_AGO', { weeks });
    } else {
      const months = Math.floor(diffInDays / 30);
      return months === 1 ? t('TIME_AGO.MONTH_AGO') : t('TIME_AGO.MONTHS_AGO', { months });
    }
  };

  // Funkcja do zapisywania kliknięcia w link
  const handleLinkClick = async () => {
    if (!currentUser || !listing.id) return;

    try {
      await firestore
        .collection('wantedListings')
        .doc(listing.id)
        .update({
          linkClicks: firebase.firestore.FieldValue.arrayUnion({
            uid: currentUser.uid,
            timestamp: new Date().toISOString(),
          }),
        });
    } catch (error) {
      console.error('Error tracking link click:', error);
    }
  };

  // Menu dla właściciela
  const ownerMenuItems = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: t('ACTIONS.EDIT'),
      onClick: onEdit,
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('ACTIONS.DELETE'),
      onClick: onDelete,
      danger: true,
    },
  ];

  return (
    <Card
      hoverable
      className="h-full min-h-[350px] transition-shadow duration-300 hover:shadow-lg flex flex-col relative pb-20"
      extra={
        isOwner && currentUser ? (
          <Dropdown menu={{ items: ownerMenuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} className="hover:bg-gray-100" />
          </Dropdown>
        ) : !isOwner ? (
          <Text type="secondary" className="text-xs">
            {formatTimeAgo(listing.createdAt)}
          </Text>
        ) : null
      }
      title={
        <div className="flex items-center gap-2 mt-2">
          <Title level={5} className="mb-0">
            <EnvironmentOutlined className="mr-2 text-gray-500" />
            {listing.location}
          </Title>
          {listing.status === 'matched' && <Tag color="success">{t('LABELS.MATCHED')}</Tag>}
        </div>
      }>
      <div className="space-y-4 flex-1 flex flex-col">
        {/* Parametry */}
        <div className="space-y-3">
          {listing.maxRent !== undefined && (
            <div className="flex items-center text-gray-600">
              <DollarOutlined className="mr-3 text-lg" />
              <div className="flex-1">
                <Text type="secondary" className="text-xs block">
                  {t('LABELS.MONTHLY_RENT')}
                </Text>
                <Text className="font-medium">
                  {t('LABELS.UP_TO')} {listing.maxRent.toLocaleString('pl-PL')} PLN/m²
                </Text>
              </div>
            </div>
          )}

          {listing.minSurface !== undefined && (
            <div className="flex items-center text-gray-600">
              <HomeOutlined className="mr-3 text-lg" />
              <div className="flex-1">
                <Text type="secondary" className="text-xs block">
                  {t('LABELS.SURFACE')}
                </Text>
                <Text className="font-medium">od {listing.minSurface} m²</Text>
              </div>
            </div>
          )}

          {listing.description && (
            <div className="flex items-start text-gray-600">
              <FileTextOutlined className="mr-3 text-lg mt-0.5" />
              <div className="flex-1">
                <Text type="secondary" className="text-xs block">
                  {t('LABELS.DESCRIPTION')}
                </Text>
                <Text className="text-sm whitespace-pre-wrap">{listing.description}</Text>
              </div>
            </div>
          )}
        </div>

        {/* Stopka z firmą - zawsze widoczna dla spójnej wysokości */}
        <div className="pt-3 border-t border-gray-100 mt-auto">
          {currentUser ? (
            listing.companyWebsite ? (
              <a
                href={formatUrl(listing.companyWebsite)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                <LinkOutlined />
                {displayDomain(listing.companyWebsite)}
              </a>
            ) : (
              <Text type="secondary" className="text-sm">
                {companyName || t('LABELS.NO_COMPANY_DATA')}
              </Text>
            )
          ) : (
            <Text type="secondary" className="text-xs italic">
              {t('LABELS.LOGIN_TO_SEE_COMPANY')}
            </Text>
          )}
        </div>
      </div>

      {/* Przycisk absolutnie pozycjonowany na dole karty */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
        {currentUser ? (
          <Button type="primary" icon={<SendOutlined />} onClick={onOfferClick} block>
            {t('ACTIONS.SEND_OFFER')}
          </Button>
        ) : (
          <div className="text-center">
            <Text type="secondary" className="text-sm italic">
              {t('LABELS.LOGIN_TO_SEND')}
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};
